import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { EYesNo } from '../../../../../shared/models/types.enum';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ContactsService } from '../../../../../shared/services/contact.service';
import { IThreatAssessmentTeam } from '../../../../../shared/models/contact';
import { ActivatedRoute } from '@angular/router';
import { UserDataService } from '../../../../../shared/services/auth/userData.service';
import { SpinnerComponent } from '../../../../components/spinner/spinner.component';

@Component({
  selector: 'threat-assessment-info',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SpinnerComponent
  ],
  templateUrl: './threat-assessment-info.component.html',
  styleUrl: './threat-assessment-info.component.css'
})
export class ThreatAssessmentInfoComponent implements OnInit, OnChanges {

  @Input() threatAssessmentSummaryData: any;
  @Output() onSaveSection1 = new EventEmitter<any>();

  yesNo = EYesNo;
  threatAssessInfoForm: FormGroup = new FormGroup([]);
  questionList = [
    {
      question: "Provide a summary of the threat or situation that was reviewed: *",
      formControlName: "situation_summary",
    },
    {
      question: "Provide a summary of the Response, Management, and Support Plan implemented by the team: *",
      formControlName: "rms_summary",
    }
  ]

  radioQuestions = [
    {
      id: 'behavioral_screen_complete_during',
      questionText: 'A Behavioral Threat Assessment Screening was completed during this assessment. *',
      formControlName: 'behavioral_screen_complete_during'
    },
    {
      id: 'full_team_threat_assessment_complete_during',
      questionText: 'A Full-Team Behavioral Threat Assessment was completed during this assessment. *',
      formControlName: 'full_team_threat_assessment_complete_during'
    }
  ];

  checkboxQuestion = {
    question: 'Copies have and/or will be sent to:',
    formControlName: 'send_to',
    answers: [ 'District Designee', 'Student Cumulative File', 'Other' ]
  };
  
  threatTeam: IThreatAssessmentTeam[] = [];
  usersList: IThreatAssessmentTeam[] = [];
  currentCaseId: string | null = null;
  currentBuildingId: string | null = null;
  messageModal: string | null = null;

  loadingContacts: boolean = false;
  loadingTeam: boolean = false;
  loading: boolean = false;

  constructor(
    private contactsService: ContactsService,
    private userDataService: UserDataService,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.initializeForm();
    this.loadUsers();
    
    this.route.paramMap.subscribe((params) => {
      this.currentCaseId = params.get('caseId');
    });

    this.loadThreatAssessmentTeamMembers();
  };
    
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['threatAssessmentSummaryData'] && this.threatAssessmentSummaryData) {
      this.initializeForm();
    }
  }

  // load all users
  loadUsers(): void {
    this.loadingContacts = true;
    this.updateLoadingState()
    this.contactsService.getUsers(this.currentBuildingId!).subscribe({
      next: (users: IThreatAssessmentTeam[]) => {
        this.usersList = users;
        // Sort the list after retrieving Users List.
        if (this.usersList.length > 0) {
          this.loadThreatAssessmentTeamMembers();
        }
        this.loadingContacts = false;
        this.updateLoadingState();
      },
      error: (error: any) => {
        this.loadingContacts = false;
        this.updateLoadingState();
        console.error('Error loading users:', error.message);
      }
    });
  };

  loadThreatAssessmentTeamMembers(): void {
    this.loadingTeam = true;
    this.updateLoadingState();
    if (this.currentCaseId) {
      this.contactsService.getThreatAssessmentTeam(this.currentCaseId).subscribe({
        next: (res) => {
          if (res.found !== false) {
            this.threatTeam = res.team
            .map((item: any) => this.usersList.find(contact => contact.user_id === item.user_id))
            .filter((contact: IThreatAssessmentTeam | undefined): contact is IThreatAssessmentTeam => contact !== undefined);
            if (this.threatTeam.length > 0) {
              this.threatTeam.sort((a, b) => a.name!.localeCompare(b.name!));
            }
            this.loadingTeam = false;
            this.updateLoadingState();
          }
        },
        error: (err) => {
          this.loadingTeam = false;
          this.updateLoadingState();
          console.error(err);
        }
      });
    }
  }

  updateLoadingState() {
    this.loading = this.loadingContacts || this.loadingTeam;
  }  
  
  private initializeForm() {
    const group: any = {};

    this.radioQuestions.forEach(question => {
      const defaultValue = this.threatAssessmentSummaryData && this.threatAssessmentSummaryData[question.formControlName] !== undefined
                           ? this.threatAssessmentSummaryData[question.formControlName]
                           : this.yesNo.No;  

      group[question.formControlName] = new FormControl(
          String(defaultValue), 
          Validators.required
      );
    });
    this.questionList.forEach(question => {
      group[question.formControlName] = new FormControl(this.threatAssessmentSummaryData?.[question.formControlName] || '', Validators.required);
    });
    group['send_to'] = new FormControl(this.threatAssessmentSummaryData?.send_to || 'District Designee', Validators.required);
    group['send_to_other'] = new FormControl(this.threatAssessmentSummaryData?.send_to_other || '');

    this.threatAssessInfoForm = new FormGroup(group);
    
    this.handleSendToOtherValidation();
  }

  private handleSendToOtherValidation() {
    this.threatAssessInfoForm.get('send_to')?.valueChanges.subscribe(value => {
      const sendToOtherControl = this.threatAssessInfoForm.get('send_to_other');
      if (value.includes('Other')) {
        sendToOtherControl?.setValidators([Validators.required]);
      } else {
        sendToOtherControl?.clearValidators();
        sendToOtherControl?.setValue('');
      }
      sendToOtherControl?.updateValueAndValidity();
    });
  }

  onCheckboxChange(e: any, controlName: string, value: string) {
    const currentValues = new Set(this.threatAssessInfoForm.get(controlName)!.value.split(',').filter((v: string) => v));
    if (e.target.checked) {
      currentValues.add(value);
    } else {
      currentValues.delete(value);
    }
    this.threatAssessInfoForm.get(controlName)?.setValue(Array.from(currentValues).join(','));
  }

  saveThreatAssessmentInfo() {
    if (this.threatAssessInfoForm.valid) {
      this.onSaveSection1.emit(this.threatAssessInfoForm.value);
    }
  }

}
